import { type QueryKey, useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

/**
 * Configuration for optimistic delete mutations.
 * Provides instant UI feedback by removing items before server confirmation.
 */
interface UseOptimisticDeleteOptions<TItem, TId = number> {
	/** The mutation function that calls the API */
	mutationFn: (id: TId) => Promise<void>
	/** Query key(s) to update optimistically and invalidate after */
	queryKey: QueryKey
	/** Extract the ID from an item for comparison */
	getId: (item: TItem) => TId
	/** Optional callback when mutation fails */
	onError?: (error: Error) => void
	/** Optional callback when mutation succeeds */
	onSuccess?: (id: TId) => void
}

/**
 * Configuration for optimistic update mutations.
 * Provides instant UI feedback by updating items before server confirmation.
 */
interface UseOptimisticUpdateOptions<TItem, TId = number, TData = Partial<TItem>> {
	/** The mutation function that calls the API */
	mutationFn: (params: { id: TId; data: TData }) => Promise<void>
	/** Query key(s) to update optimistically and invalidate after */
	queryKey: QueryKey
	/** Extract the ID from an item for comparison */
	getId: (item: TItem) => TId
	/** Apply the update data to an item */
	applyUpdate: (item: TItem, data: TData) => TItem
	/** Optional callback when mutation fails */
	onError?: (error: Error) => void
	/** Optional callback when mutation succeeds */
	onSuccess?: (id: TId) => void
}

/**
 * Hook for optimistic delete mutations.
 *
 * Immediately removes the item from the UI, then:
 * - On success: invalidates the query to sync with server
 * - On error: rolls back to previous state and calls onError
 *
 * @example
 * ```typescript
 * const deletePlay = useOptimisticDelete({
 *   mutationFn: (id) => fetch(`/api/plays/${id}`, { method: 'DELETE' }),
 *   queryKey: playKeys.list(playbookId),
 *   getId: (play) => play.id,
 *   onError: (error) => toast.error('Failed to delete play'),
 * })
 *
 * // Usage
 * await deletePlay(playId)
 * ```
 */
export function useOptimisticDelete<TItem, TId = number>({
															 mutationFn,
															 queryKey,
															 getId,
															 onError,
															 onSuccess,
														 }: UseOptimisticDeleteOptions<TItem, TId>) {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationFn,
		onMutate: async (id: TId) => {
			// Cancel any outgoing refetches to prevent race conditions
			await queryClient.cancelQueries({ queryKey })

			// Snapshot the previous value for rollback
			const previous = queryClient.getQueryData(queryKey)

			// Optimistically remove the item from the cache
			// Handle both paginated { items: [...] } and plain array structures
			queryClient.setQueryData(queryKey, (old: unknown) => {
				// Handle paginated structure { items: [...] }
				if (old && typeof old === 'object' && 'items' in old) {
					const typed = old as { items: TItem[]; nextCursor?: string; hasMore: boolean }
					return {
						...typed,
						items: typed.items.filter((item) => getId(item) !== id),
					}
				}
				// Handle plain array (legacy)
				if (Array.isArray(old)) {
					return old.filter((item: TItem) => getId(item) !== id)
				}
				return old
			})

			return { previous }
		},
		onError: (error, _id, context) => {
			// Rollback to the previous value on error
			if (context?.previous) {
				queryClient.setQueryData(queryKey, context.previous)
			}
			onError?.(error as Error)
		},
		onSuccess: (_data, id) => {
			onSuccess?.(id)
		},
		onSettled: () => {
			// Always invalidate to ensure consistency with server
			queryClient.invalidateQueries({ queryKey })
		},
	})

	// Return a simple async function for cleaner API
	return useCallback(
		async (id: TId): Promise<void> => {
			await mutation.mutateAsync(id)
		},
		[mutation],
	)
}

/**
 * Hook for optimistic update mutations.
 *
 * Immediately updates the item in the UI, then:
 * - On success: invalidates the query to sync with server
 * - On error: rolls back to previous state and calls onError
 *
 * @example
 * ```typescript
 * const updatePlay = useOptimisticUpdate({
 *   mutationFn: ({ id, data }) => fetch(`/api/plays/${id}`, {
 *     method: 'PATCH',
 *     body: JSON.stringify(data),
 *   }),
 *   queryKey: playKeys.list(playbookId),
 *   getId: (play) => play.id,
 *   applyUpdate: (play, data) => ({ ...play, ...data }),
 * })
 *
 * // Usage
 * await updatePlay(playId, { name: 'New Name' })
 * ```
 */
export function useOptimisticUpdate<TItem, TId = number, TData = Partial<TItem>>({
																					 mutationFn,
																					 queryKey,
																					 getId,
																					 applyUpdate,
																					 onError,
																					 onSuccess,
																				 }: UseOptimisticUpdateOptions<TItem, TId, TData>) {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationFn,
		onMutate: async ({ id, data }: { id: TId; data: TData }) => {
			// Cancel any outgoing refetches to prevent race conditions
			await queryClient.cancelQueries({ queryKey })

			// Snapshot the previous value for rollback
			const previous = queryClient.getQueryData(queryKey)

			// Optimistically update the item in the cache
			// Handle both paginated { items: [...] } and plain array structures
			queryClient.setQueryData(queryKey, (old: unknown) => {
				// Handle paginated structure { items: [...] }
				if (old && typeof old === 'object' && 'items' in old) {
					const typed = old as { items: TItem[]; nextCursor?: string; hasMore: boolean }
					return {
						...typed,
						items: typed.items.map((item) => (getId(item) === id ? applyUpdate(item, data) : item)),
					}
				}
				// Handle plain array (legacy)
				if (Array.isArray(old)) {
					return old.map((item: TItem) => (getId(item) === id ? applyUpdate(item, data) : item))
				}
				return old
			})

			return { previous }
		},
		onError: (error, _vars, context) => {
			// Rollback to the previous value on error
			if (context?.previous) {
				queryClient.setQueryData(queryKey, context.previous)
			}
			onError?.(error as Error)
		},
		onSuccess: (_data, { id }) => {
			onSuccess?.(id)
		},
		onSettled: () => {
			// Always invalidate to ensure consistency with server
			queryClient.invalidateQueries({ queryKey })
		},
	})

	// Return a simple async function for cleaner API
	return useCallback(
		async (id: TId, data: TData): Promise<void> => {
			await mutation.mutateAsync({ id, data })
		},
		[mutation],
	)
}

/**
 * Hook that returns both delete and update mutations for an entity.
 * Convenience wrapper when you need both operations.
 *
 * @example
 * ```typescript
 * const { deleteMutation, updateMutation } = useOptimisticMutations({
 *   queryKey: playKeys.list(playbookId),
 *   getId: (play) => play.id,
 *   deleteFn: (id) => apiDeletePlay(id),
 *   updateFn: ({ id, data }) => apiUpdatePlay(id, data),
 *   applyUpdate: (play, data) => ({ ...play, ...data }),
 *   onError: handleUnauthorized,
 * })
 * ```
 */
export function useOptimisticMutations<TItem, TId = number, TData = Partial<TItem>>({
																						queryKey,
																						getId,
																						deleteFn,
																						updateFn,
																						applyUpdate,
																						onDeleteError,
																						onUpdateError,
																						onDeleteSuccess,
																						onUpdateSuccess,
																					}: {
	queryKey: QueryKey
	getId: (item: TItem) => TId
	deleteFn: (id: TId) => Promise<void>
	updateFn?: (params: { id: TId; data: TData }) => Promise<void>
	applyUpdate?: (item: TItem, data: TData) => TItem
	onDeleteError?: (error: Error) => void
	onUpdateError?: (error: Error) => void
	onDeleteSuccess?: (id: TId) => void
	onUpdateSuccess?: (id: TId) => void
}) {
	const deleteMutation = useOptimisticDelete<TItem, TId>({
		mutationFn: deleteFn,
		queryKey,
		getId,
		onError: onDeleteError,
		onSuccess: onDeleteSuccess,
	})

	const hasUpdate = !!(updateFn && applyUpdate)
	const noopMutationFn = async () => {}
	const identityApply = (item: TItem) => item

	const updateMutationHook = useOptimisticUpdate<TItem, TId, TData>({
		mutationFn: updateFn ?? noopMutationFn as (params: { id: TId; data: TData }) => Promise<void>,
		queryKey,
		getId,
		applyUpdate: applyUpdate ?? identityApply as (item: TItem, data: TData) => TItem,
		onError: onUpdateError,
		onSuccess: onUpdateSuccess,
	})

	return {
		deleteMutation,
		updateMutation: hasUpdate ? updateMutationHook : undefined,
	}
}
